import type { AuthenticationResponseJSON } from "@simplewebauthn/server";
import { z } from "zod";

export const priceSchemaI18n = (t?: (key: "errors.max2digits") => string) =>
  z
    .number()
    .positive()
    .max(99999.99)
    .refine((val) => parseFloat(val.toFixed(2)) === val, {
      error: t ? t("errors.max2digits") : "Must have at most 2 decimal places",
    });

export const priceSchema = priceSchemaI18n();

export const priceOptionsSchemaI18n = (
  t?: (key: "errors.required" | "errors.unique" | "errors.max2digits") => string,
) =>
  z
    .array(priceSchemaI18n(t))
    .min(1, { error: t ? t("errors.required") : undefined })
    .refine((arr) => arr.length === new Set(arr).size, {
      error: t ? t("errors.unique") : "Must be unique",
    });

export const priceOptionsSchema = priceOptionsSchemaI18n();

// https://w3c.github.io/webauthn/#dictdef-authenticationresponsejson
// A string is a Treezor sandbox scaProof from simulateTreezorScaProof, forwarded as is.
export const authenticationResponseJSONSchema = z.union([
  z.object({
    id: z.base64url(),
    rawId: z.base64url(),
    response: z.object({
      clientDataJSON: z.base64url(),
      authenticatorData: z.base64url(),
      signature: z.base64url(),
      userHandle: z.base64url().optional(),
    }),
    authenticatorAttachment: z.enum(["cross-platform", "platform"]).optional(),
    clientExtensionResults: z.object({
      appid: z.boolean().optional(),
      credProps: z
        .object({
          rk: z.boolean().optional(),
        })
        .optional(),
      hmacCreateSecret: z.boolean().optional(),
    }),
    type: z.literal("public-key"),
  }),
  z.string(),
]) satisfies z.ZodType<AuthenticationResponseJSON | string>;
